import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { resolveText } from '../../utils/bilingualResolver';
import LanguageToggle from '../LanguageToggle';
import styles from './ChapterNotes.module.css';

/**
 * Chapter Notes viewer — full bilingual support.
 * On first visit to a chapter in Hindi: translates via Google Translate
 * (6 concurrent requests, ~2-4 sec). On repeat visits: instant from cache.
 */

function Block({ block, language = 'english' }) {
  switch (block.type) {
    case 'paragraph':
      return <p className={styles.paragraph}>{renderInline(block.text, language)}</p>;

    case 'definition':
      return (
        <div className={styles.calloutDef}>
          <div className={styles.calloutLabel}>Definition</div>
          <div>
            <strong>{renderInline(block.term || block.title, language)}.</strong> {renderInline(block.body || block.text, language)}
          </div>
        </div>
      );

    case 'theorem':
      return (
        <div className={styles.calloutThm}>
          <div className={styles.calloutLabel}>
            Theorem{block.name ? ` — ${renderPlainText(block.name, language)}` : ''}
          </div>
          <div>{renderInline(block.statement, language)}</div>
          {block.proof && (
            <details className={styles.proof}>
              <summary>Proof</summary>
              <div>{renderInline(block.proof, language)}</div>
            </details>
          )}
        </div>
      );

    case 'equation':
      {
        const mathExpr =
          typeof block.latex === 'string'
            ? block.latex
            : typeof block.equation === 'string'
              ? block.equation
              : typeof block.statement === 'string'
                ? block.statement
                : typeof block.text === 'string'
                  ? block.text
              : '';

        return (
          <div className={styles.equationWrap}>
            <BlockMath math={stripMathDelimiters(mathExpr)} />
            {block.label && <span className={styles.eqLabel}>({block.label})</span>}
          </div>
        );
      }

    case 'figure':
      return (
        <figure className={styles.figure}>
          <div
            className={styles.figureBox}
            dangerouslySetInnerHTML={{ __html: block.svg }}
          />
          {block.caption && (
            <figcaption className={styles.caption}>
              <strong>Fig.</strong> {renderInline(block.caption, language)}
            </figcaption>
          )}
        </figure>
      );

    case 'example':
      return (
        <div className={styles.example}>
          <div className={styles.exampleLabel}>
            Example{block.number ? ` ${block.number}` : ''}
          </div>
          <div className={styles.exampleProblem}>{renderInline(block.problem, language)}</div>
          <details className={styles.exampleSol} open={block.openSolution}>
            <summary>Solution</summary>
            <div>{renderInline(block.solution, language)}</div>
          </details>
        </div>
      );

    case 'note':
      return <div className={styles.note}>📝 {renderInline(block.text || block.body, language)}</div>;
    case 'tip':
      return <div className={styles.tip}>💡 {renderInline(block.text || block.body, language)}</div>;
    case 'warning':
      return <div className={styles.warning}>⚠️ {renderInline(block.text || block.body, language)}</div>;

    case 'list':
      return block.ordered ? (
        <ol className={styles.list}>
          {block.items.map((it, i) => (
            <li key={i}>{renderInline(it, language)}</li>
          ))}
        </ol>
      ) : (
        <ul className={styles.list}>
          {block.items.map((it, i) => (
            <li key={i}>{renderInline(it, language)}</li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i}>{renderInline(h, language)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j}>{renderInline(c, language)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'divider':
      return <hr className={styles.divider} />;

    default:
      return null;
  }
}

/**
 * renderInline: takes a string OR an array of nodes. Strings may contain
 * inline LaTeX delimited by $...$ which is converted to <InlineMath/>.
 * Also supports **bold** markers.
 */
function renderInline(input, language = 'english') {
  if (input == null) return null;
  const resolved = resolveText(input, language);
  if (Array.isArray(resolved)) return resolved.map((p, i) => <React.Fragment key={i}>{renderInline(p, language)}</React.Fragment>);
  if (typeof resolved !== 'string') return null;

  const normalized = normalizeMathDelimiters(resolved);
  const boldParts = normalized.split(/(\*\*[\s\S]+?\*\*)/g);

  return boldParts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      const inner = part.slice(2, -2);
      return <strong key={`b-${i}`}>{renderMathRuns(inner, `b-${i}`)}</strong>;
    }
    return <React.Fragment key={`p-${i}`}>{renderMathRuns(part, `p-${i}`)}</React.Fragment>;
  });
}

function renderMathRuns(text, keyPrefix) {
  if (!text) return null;

  // Split by display $$...$$ or inline $...$ math.
  const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^$\n]+\$)/g);
  return parts.map((part, i) => {
    if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
      return (
        <span key={`${keyPrefix}-d-${i}`} className={styles.inlineBlockMath}>
          <BlockMath math={part.slice(2, -2)} />
        </span>
      );
    }

    if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
      return <InlineMath key={`${keyPrefix}-i-${i}`} math={part.slice(1, -1)} />;
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

function normalizeMathDelimiters(text) {
  return text
    // Common mojibake cleanups seen in notes content exports.
    .replace(/窶・|窶溺/g, '-')
    .replace(/竊・/g, ' -> ')
    .replace(/竊誕/g, '\\to a')
    .replace(/竕､/g, '<=')
    .replace(/竕･/g, '>=')
    // Support escaped dollar delimiters coming from data strings.
    .replace(/\\\$/g, '$')
    // Convert paired LaTeX inline delimiters \( ... \) to $...$.
    .replace(/\\\(([\s\S]+?)\\\)/g, (_m, inner) => `$${inner}$`)
    // Convert paired LaTeX display delimiters \[ ... \] to $$...$$.
    .replace(/\\\[([\s\S]+?)\\\]/g, (_m, inner) => `$$${inner}$$`)
    // Convert display math delimiters to inline-compatible tokens for mixed text.
    .replace(/\$\$([\s\S]+?)\$\$/g, (_m, inner) => `$$${inner}$$`);
}

function stripMathDelimiters(text) {
  if (typeof text !== 'string') return '';

  const trimmed = text.trim();
  if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length > 4) {
    return trimmed.slice(2, -2).trim();
  }
  if (trimmed.startsWith('$') && trimmed.endsWith('$') && trimmed.length > 2) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function renderPlainText(input, language = 'english') {
  const resolved = resolveText(input, language);
  if (resolved == null) return '';
  if (typeof resolved !== 'string') return String(resolved);
  return resolved.replace(/\$\$([\s\S]+?)\$\$/g, '$1').replace(/\$([^$\n]+)\$/g, '$1');
}

function isTextHeavyFormula(input) {
  if (typeof input !== 'string') return false;

  const math = stripMathDelimiters(input);
  const textMatches = [...math.matchAll(/\\text\{([^}]*)\}/g)];
  const textLength = textMatches.reduce((sum, match) => sum + match[1].trim().length, 0);

  return (
    textLength >= 12 &&
    !/\\(?:d?frac|sum|prod|int|sqrt|begin|end|matrix|binom|Gamma|alpha|beta|theta|lambda|mu|sigma|pi|Delta)/.test(math)
  );
}

function toUnicodeScript(value, map) {
  return [...value].map((char) => map[char] || char).join('');
}

function latexToReadableText(input) {
  if (typeof input !== 'string') return '';

  const subMap = {
    '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
    '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
    '+': '₊', '-': '₋', '(': '₍', ')': '₎', 'n': 'ₙ', 'i': 'ᵢ', 'j': 'ⱼ',
  };
  const supMap = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾', 'n': 'ⁿ', 'i': 'ⁱ',
  };

  return stripMathDelimiters(input)
    .replace(/\\text\{([^}]*)\}/g, '$1')
    .replace(/\\\{/g, '{')
    .replace(/\\\}/g, '}')
    .replace(/\\times/g, ' × ')
    .replace(/\\equiv/g, ' ≡ ')
    .replace(/\\approx/g, ' ≈ ')
    .replace(/\\AA/g, ' Å ')
    .replace(/\\prime/g, '′')
    .replace(/\\to/g, ' → ')
    .replace(/\\Rightarrow/g, ' ⇒ ')
    .replace(/\\left|\\right/g, '')
    .replace(/\\,/g, ' ')
    .replace(/\\;/g, ' ')
    .replace(/\\%/g, '%')
    .replace(/_\{([^}]+)\}/g, (_m, value) => toUnicodeScript(value, subMap))
    .replace(/\^\{([^}]+)\}/g, (_m, value) => toUnicodeScript(value, supMap))
    .replace(/_([A-Za-z0-9+-])/g, (_m, value) => toUnicodeScript(value, subMap))
    .replace(/\^([A-Za-z0-9+-])/g, (_m, value) => toUnicodeScript(value, supMap))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export default function ChapterNotes({ chapter }) {
  const location = useLocation();
  const returnSection = location.state?.returnSection || 'chapterwise';
  const returnTab = location.state?.returnTab || (returnSection === 'handbooks' ? 'handbooks' : returnSection === 'cheatsheets' ? 'sheets' : 'chapterwise');
  const returnHash = location.state?.returnHash || (returnSection === 'handbooks' ? '#master-handbooks' : returnSection === 'cheatsheets' ? '#cheatsheets' : '#chapterwise-notes');
  const isSchoolNotes = returnSection === 'school-notes';
  const returnPath = isSchoolNotes ? '/school-notes' : `/study-materials${returnHash}`;
  const [activeId, setActiveId] = useState(chapter.sections[0]?.id);
  const [language, setLanguage] = useState('english');
  const [isTocStuck, setIsTocStuck] = useState(false);
  const sectionRefs = useRef({});
  const heroRef = useRef(null);

  // Deterministic bilingual mode:
  // Uses only hardcoded { english, hindi } values from content files.
  // No runtime translation API calls.
  const translatedChapter = useMemo(
    () => resolveText(chapter, language),
    [chapter, language]
  );

  // Scroll-spy for active section in TOC
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [chapter]);

  // TOC should stick only after the header area has moved up past navbar offset.
  useEffect(() => {
    const updateTocStickState = () => {
      if (!heroRef.current || window.innerWidth <= 900) {
        setIsTocStuck(false);
        return;
      }

      const rootStyles = getComputedStyle(document.documentElement);
      const navHeightVar = parseInt(rootStyles.getPropertyValue('--nav-height-total'), 10);
      const navTop = Number.isFinite(navHeightVar) ? navHeightVar : 106;
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;

      // Stick only after header has moved above navbar line; unstick when header re-enters view.
      setIsTocStuck(heroBottom <= navTop + 8);
    };

    updateTocStickState();
    window.addEventListener('scroll', updateTocStickState, { passive: true });
    window.addEventListener('resize', updateTocStickState);

    return () => {
      window.removeEventListener('scroll', updateTocStickState);
      window.removeEventListener('resize', updateTocStickState);
    };
  }, [chapter]);

  const allFormulas = useMemo(
    () => translatedChapter.keyFormulas || [],
    [translatedChapter]
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <header ref={heroRef} className={styles.hero}>
        <div className={styles.crumbs}>
          <Link to={isSchoolNotes ? '/school-notes' : '/study-materials'}>{isSchoolNotes ? 'School Notes' : 'Study Materials'}</Link>
          <span>›</span>
          <Link to={returnPath} state={{ returnSection, returnTab }}>{isSchoolNotes ? 'School Notes' : 'JEE/NEET Notes'}</Link>
          <span>›</span>
          <span className={styles.crumbCurrent}>{resolveText(translatedChapter.title, language)}</span>
        </div>
        <h1 className={styles.title}>{resolveText(translatedChapter.title, language)}</h1>
        {translatedChapter.subtitle && <p className={styles.subtitle}>{renderInline(translatedChapter.subtitle, language)}</p>}
        <div className={styles.heroControls}>
          {chapter.syllabusTags && chapter.syllabusTags.length > 0 && (
            <div className={styles.tags}>
              {chapter.syllabusTags.map((t) => (
                <span key={t} className={styles.tag}>
                  #{t}
                </span>
              ))}
            </div>
          )}
          <LanguageToggle language={language} onChange={setLanguage} />
        </div>
      </header>

      {/* Two-column layout */}
      <div className={styles.layout}>
        {/* TOC */}
        <aside className={`${styles.toc} ${isTocStuck ? styles.tocStuck : ''}`}>
          <div className={styles.tocSticky}>
            <div className={styles.tocTitle}>{language === 'hindi' ? 'इस पृष्ठ पर' : 'On this page'}</div>
            <ol className={styles.tocList}>
              {translatedChapter.sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className={`${styles.tocLink} ${
                      activeId === s.id ? styles.tocLinkActive : ''
                    }`}
                  >
                    {renderInline(s.title, language)}
                  </a>
                </li>
              ))}
              {allFormulas.length > 0 && (
                <li>
                  <a
                    href="#key-formulas"
                    className={`${styles.tocLink} ${
                      activeId === 'key-formulas' ? styles.tocLinkActive : ''
                    }`}
                  >
                    {language === 'hindi' ? 'मुख्य सूत्र' : 'Key Formulas'}
                  </a>
                </li>
              )}
              {translatedChapter.pyqs && translatedChapter.pyqs.length > 0 && (
                <li>
                  <a
                    href="#pyqs"
                    className={`${styles.tocLink} ${
                      activeId === 'pyqs' ? styles.tocLinkActive : ''
                    }`}
                  >
                    {language === 'hindi' ? 'JEE पिछले साल के प्रश्न' : 'JEE PYQs'}
                  </a>
                </li>
              )}
            </ol>
          </div>
        </aside>

        {/* Main content */}
        <main className={styles.content}>
          {translatedChapter.sections.map((section, idx) => (
            <section
              key={section.id}
              id={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
              className={styles.section}
            >
              <h2 className={styles.sectionHeading}>
                <span className={styles.sectionNum}>{idx + 1}.</span> {renderInline(section.title, language)}
              </h2>
              {section.intro && (
                <p className={styles.sectionIntro}>{renderInline(section.intro, language)}</p>
              )}
              {section.blocks && section.blocks.length > 0 ? (
                section.blocks.map((b, i) => (
                  <Block key={i} block={b} language={language} />
                ))
              ) : (
                <p className={styles.paragraph}>{language === 'hindi' ? 'सामग्री लोड हो रही है...' : 'Content is loading...'}</p>
              )}
            </section>
          ))}

          {/* Key formulas */}
          {allFormulas.length > 0 && (
            <section
              id="key-formulas"
              ref={(el) => (sectionRefs.current['key-formulas'] = el)}
              className={styles.section}
            >
              <h2 className={styles.sectionHeading}>
                <span className={styles.sectionNum}>★</span> {language === 'hindi' ? 'एक नज़र में मुख्य सूत्र' : 'Key Formulas at a Glance'}
              </h2>
              <div className={styles.formulaGrid}>
                {allFormulas.map((f, i) => (
                  <div key={i} className={styles.formulaCard}>
                    {translatedChapter.subject === 'biology' && isTextHeavyFormula(f.latex || f.equation || '') ? (
                      <div className={styles.formulaText}>{latexToReadableText(f.latex || f.equation || '')}</div>
                    ) : (
                      <div className={styles.formulaEquationWrap}>
                        <BlockMath math={stripMathDelimiters(f.latex || f.equation || '')} />
                      </div>
                    )}
                    {f.desc && <div className={styles.formulaDesc}>{renderInline(f.desc, language)}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* PYQs */}
          {translatedChapter.pyqs && translatedChapter.pyqs.length > 0 && (
            <section
              id="pyqs"
              ref={(el) => (sectionRefs.current['pyqs'] = el)}
              className={styles.section}
            >
              <h2 className={styles.sectionHeading}>
                <span className={styles.sectionNum}>📝</span> {language === 'hindi' ? 'JEE पिछले साल के प्रश्न' : 'JEE Previous Year Questions'}
              </h2>
              {translatedChapter.pyqs.map((q, i) => (
                <div key={i} className={styles.pyq}>
                  <div className={styles.pyqHead}>
                    <span className={styles.pyqYear}>
                      {q.exam || 'JEE'} {q.year}
                    </span>
                    {q.tag && <span className={styles.pyqTag}>{q.tag}</span>}
                  </div>
                  <div className={styles.pyqProblem}>
                    <strong>Q{i + 1}.</strong> {renderInline(q.problem, language)}
                  </div>
                  {q.options && (
                    <ol type="A" className={styles.pyqOptions}>
                      {q.options.map((o, j) => (
                        <li key={j}>{renderInline(o, language)}</li>
                      ))}
                    </ol>
                  )}
                  {q.answer && (
                    <div className={styles.pyqAnswer}>
                      <strong>{language === 'hindi' ? 'उत्तर:' : 'Answer:'}</strong> {renderInline(q.answer, language)}
                    </div>
                  )}
                  {q.solution && (
                    <details className={styles.pyqSol}>
                      <summary>{language === 'hindi' ? 'समाधान' : 'Solution'}</summary>
                      <div>{renderInline(q.solution, language)}</div>
                    </details>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Footer nav */}
          <div className={styles.footerNav}>
            <Link to={returnPath} state={{ returnSection, returnTab }} className={styles.backBtn}>
              {language === 'hindi' ? '← सभी अध्याय' : '← All Chapters'}
            </Link>
            {translatedChapter.next && (
              <Link
                to={`/notes/${chapter.subject}/${chapter.next.slug}`}
                state={{ returnSection, returnTab, returnHash }}
                className={styles.nextBtn}
              >
                {language === 'hindi' ? 'अगला' : 'Next'}: {resolveText(translatedChapter.next.title, language)} →
              </Link>
            )}
          </div>
        </main>
      </div>

    </div>
  );
}
